import { useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  Car,
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
} from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { useUiStore } from '../store/ui';
import { fmtDate, initials } from '../lib/dates';
import { Student } from '../types';

function StudentCard({ student }: { student: Student }) {
  const openBilan = useUiStore((s) => s.openBilan);
  const openLivret = useUiStore((s) => s.openLivret);
  const name = `${student.firstName} ${student.lastName}`;
  const pct = Math.min(
    100,
    Math.round((student.drivingHoursDone / student.drivingHoursTarget) * 100),
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
          {initials(student.firstName, student.lastName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">{name}</span>
            {student.readyForExam && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-[11px] font-semibold">
                <CheckCircle2 size={12} />
                Prêt(e) pour l'examen
              </span>
            )}
            {student.examDate && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px] font-semibold">
                <GraduationCap size={12} />
                Examen le {fmtDate(student.examDate)}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Permis B ·{' '}
            {student.vehicleType === 'automatic' ? 'Boîte automatique' : 'Boîte manuelle'}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-slate-600">Heures de conduite</span>
        <span className="text-sm font-semibold text-slate-900">
          {student.drivingHoursDone}/{student.drivingHoursTarget}h
        </span>
      </div>
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="space-y-1.5 mb-5">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Car size={14} className="text-slate-400 flex-shrink-0" />
          <span>
            {student.nextLessonAt
              ? `Prochaine leçon : ${fmtDate(student.nextLessonAt)}`
              : 'Aucune leçon planifiée'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <GraduationCap size={14} className="text-slate-400 flex-shrink-0" />
          <span>
            Examen :{' '}
            <span className="font-semibold text-slate-900">
              {student.examDate ? fmtDate(student.examDate) : 'à planifier'}
            </span>
          </span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={() => openBilan({ candidateId: student.id, candidateName: name })}
          className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors flex items-center justify-center gap-2"
        >
          <ClipboardCheck size={14} />
          Bilan de leçon
        </button>
        <button
          onClick={() => openLivret(student.id)}
          className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
        >
          <BookOpen size={14} />
          Voir le livret
        </button>
      </div>
    </div>
  );
}

export default function StudentsView() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['students'],
    queryFn: instructorApi.getStudents,
  });

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-slate-900">Mes élèves</h1>
        <p className="text-slate-600">
          {data ? `${data.length} élève(s) vous sont confiés.` : 'Suivi de vos élèves.'}
        </p>
      </div>

      {isLoading && <p className="text-slate-500">Chargement de vos élèves…</p>}
      {isError && (
        <p className="text-red-600">
          Impossible de charger vos élèves : {(error as Error).message}
        </p>
      )}

      {data && data.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          Aucun élève ne vous est rattaché pour le moment.
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {data.map((s) => (
            <StudentCard key={s.id} student={s} />
          ))}
        </div>
      )}
    </section>
  );
}
